import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchUserProfile } from '../lib/api';

// Criando o contexto de autenticação
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Função para carregar os dados do usuário
  const loadUser = useCallback(async () => {
    try {
      // Verificar token no localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Buscar dados do usuário
      const userData = await fetchUserProfile();
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      // Se o erro for 401 (não autorizado), remover token inválido
      if (err.message && (err.message.includes('401') || err.message.includes('Credenciais inválidas'))) {
        localStorage.removeItem('token');
        setUser(null);
      } else {
        setError('Erro ao carregar dados do usuário');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Função para carregar os dados do usuário inicialmente
    const initializeAuth = async () => {
      try {
        // Verificar token na URL (após callback do OAuth)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        
        if (tokenParam) {
          localStorage.setItem('token', tokenParam);
          
          // Limpar o token da URL antes de carregar usuário
          const currentUrl = window.location.pathname;
          window.history.replaceState({}, document.title, currentUrl);
          
          // Carregar usuário imediatamente após salvar token
          const userData = await fetchUserProfile();
          setUser(userData);
          setLoading(false);
          return;
        }

        // Se não há token na URL, tentar carregar do localStorage
        await loadUser();
      } catch (err) {
        console.error('Erro ao inicializar autenticação:', err);
        setError('Erro ao carregar dados do usuário');
        localStorage.removeItem('token');
        setLoading(false);
      }
    };

    initializeAuth();
  }, [loadUser]);

  // Função para logout
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/login';
  };

  // Valores disponibilizados pelo contexto
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    logout,
    refreshUser: loadUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};